/**
 * MatchStats — the icon indicator row under the minimap, top-right.
 *
 * Three compact cells, each an icon plus a number: eliminations, deaths, and
 * players remaining. Damage dealt is deliberately absent — it is a post-match
 * number and lives on the game end screen, not on the HUD during play.
 *
 * create(hud) builds the PIXI objects into hud.elements.matchStats,
 * position(hud) lays them out for hud.screenWidth/Height, update(hud, …) redraws
 * from game state. `hud` is the PixiHUDRenderer coordinator: it owns
 * hudContainer, elements, screen size, _createText() and send().
 *
 * Layout is anchored to the minimap panel (see MINIMAP_MARGIN/panelSize), so the
 * row stays welded to the map's bottom edge at any screen size.
 */
import * as PIXI from 'pixi.js';
import { MINIMAP_DEFAULTS } from './minimapMath.js';
import { PANEL_MARGIN as MINIMAP_MARGIN } from './Minimap.js';
import * as statIcons from './statIcons.js';

const ROW_HEIGHT = 34;
const GAP_BELOW_MINIMAP = 10;
const ICON_SIZE = 16;

/** Cell order left-to-right, matching the icon asset names. */
const CELLS = [
  { key: 'eliminations', icon: 'eliminations', color: 0xffd23f },
  { key: 'deaths', icon: 'deaths', color: 0xff6b6b },
  { key: 'players', icon: 'players', color: 0xffffff }
];

/**
 * Create the stat indicator row
 */
export function create(hud) {
  const container = new PIXI.Container();
  container.zIndex = 50; // sit with the minimap, above the plain HUD layer
  hud.hudContainer.addChild(container);

  const background = new PIXI.Graphics();
  container.addChild(background);

  statIcons.preload();

  const cells = CELLS.map(({ key, icon, color }) => {
    const cell = new PIXI.Container();

    const sprite = new PIXI.Sprite();
    sprite.anchor.set(0.5, 0.5);
    sprite.width = ICON_SIZE;
    sprite.height = ICON_SIZE;
    sprite.tint = color;
    sprite.visible = false; // until its texture loads
    cell.addChild(sprite);

    const valueText = hud._createText('0', 15, color, true);
    valueText.anchor.set(0, 0.5);
    cell.addChild(valueText);

    container.addChild(cell);
    return { key, icon, container: cell, sprite, valueText };
  });

  hud.elements.matchStats = { container, background, cells };
  hud._positionMatchStats();
}

/**
 * Whether the Deaths cell should be displayed on the HUD.
 * Build Royale / Battle Royale modes hide Deaths, displaying only Eliminations and Players remaining.
 * Respawn modes (FFA, TDM, Gun Game) display Deaths.
 */
export function shouldShowDeaths(gameState) {
  const mode = gameState?.gameModeConfig;
  if (!mode) return false; // Default mode in DriftShot is Battle Royale
  if (mode.type === 'br' || mode.id === 'battle_royale' || mode.id === 'battle_royale_ranked' || mode.allowRespawn === false) {
    return false;
  }
  return true;
}

/**
 * Position the row directly beneath the minimap in the top-right corner
 */
export function position(hud, gameState = hud.gameState) {
  const ms = hud.elements.matchStats;
  if (!ms) return;

  const panelSize = hud.elements.minimap?.size || MINIMAP_DEFAULTS.panelSize;
  const rowWidth = panelSize;

  ms.container.x = hud.screenWidth - rowWidth - MINIMAP_MARGIN;
  ms.container.y = MINIMAP_MARGIN + panelSize + GAP_BELOW_MINIMAP;

  ms.background.clear();
  ms.background.roundRect(0, 0, rowWidth, ROW_HEIGHT, 5);
  ms.background.fill({ color: 0x000000, alpha: 0.675 });

  const showDeaths = shouldShowDeaths(gameState);
  const visibleCells = ms.cells.filter((cell) => cell.key !== 'deaths' || showDeaths);
  const hiddenCells = ms.cells.filter((cell) => cell.key === 'deaths' && !showDeaths);

  hiddenCells.forEach((cell) => {
    cell.container.visible = false;
  });

  // Even fractions across visible cells; icon and number are centred together inside each cell.
  const cellWidth = rowWidth / visibleCells.length;
  visibleCells.forEach((cell, index) => {
    cell.container.visible = true;
    cell.container.x = cellWidth * index;
    cell.container.y = 0;

    cell.sprite.x = cellWidth / 2 - 10;
    cell.sprite.y = ROW_HEIGHT / 2;

    cell.valueText.x = cellWidth / 2 + 2;
    cell.valueText.y = ROW_HEIGHT / 2;
  });
}

/**
 * Update the stat indicator row
 * @param {Object} gameState - Game state
 */
export function update(hud, gameState) {
  const ms = hud.elements.matchStats;
  if (!ms) return;

  // Keep the row welded to the minimap when its configured size changes,
  // or re-position when the mode's death visibility changes.
  const panelSize = hud.elements.minimap?.size || MINIMAP_DEFAULTS.panelSize;
  const showDeaths = shouldShowDeaths(gameState);
  if (ms.panelSize !== panelSize || ms.showDeaths !== showDeaths) {
    ms.panelSize = panelSize;
    ms.showDeaths = showDeaths;
    position(hud, gameState);
  }

  // Count alive players (the "players remaining" cell).
  // Use authoritative count from server snapshot (which includes all players + bots across the map),
  // falling back to local alive count if snapshot hasn't arrived yet.
  let localAliveCount = 0;
  for (const [, player] of gameState.players) {
    if (!player.isDead) {
      localAliveCount++;
    }
  }

  // null until the first server snapshot (see GameState.alivePlayers); 0 is a valid authoritative count.
  const alivePlayers = gameState.alivePlayers ?? localAliveCount;

  const localPlayer = gameState.players.get(gameState.localId);
  const values = {
    eliminations: Math.round(localPlayer?.stats?.kills || 0),
    deaths: Math.round(localPlayer?.stats?.deaths || 0),
    players: alivePlayers
  };

  for (const cell of ms.cells) {
    cell.valueText.text = String(values[cell.key] ?? 0);

    // Textures arrive asynchronously; adopt each the frame after it lands.
    if (!cell.sprite.visible && (cell.key !== 'deaths' || showDeaths)) {
      const texture = statIcons.get(cell.icon);
      if (texture) {
        cell.sprite.texture = texture;
        cell.sprite.width = ICON_SIZE;
        cell.sprite.height = ICON_SIZE;
        cell.sprite.visible = true;
      }
    }
  }
}
